import { Injectable, inject, signal } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';
import { environment } from '@environments/environment';
import { APP } from '@environments/app.config';
import { ApiService } from './api.service';
import type { ApiResponse } from '@core/models/api-response.model';
import type { SchoolBranding } from '@core/models/platform.model';

/**
 * Dynamic per-school branding.
 *
 * Overrides the app's CSS custom properties on :root so the ENTIRE portal
 * (which already consumes `var(--color-primary*)`) re-themes to the current
 * school's accent colour, and swaps the logo/name/title. Loaded either from a
 * public slug (login page, pre-auth) or from the authenticated tenant-branding
 * endpoint (after login).
 *
 * Product default = Padhaaii branding (from APP config — never hardcoded).
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
  private readonly api = inject(ApiService);
  // Raw client (bypasses the auth interceptor) for the PUBLIC branding endpoint.
  private readonly rawHttp = new HttpClient(inject(HttpBackend));

  readonly branding = signal<SchoolBranding | null>(null);
  readonly isLoaded = signal(false);

  /** Product-default branding (super admin / logged-out / unknown school). */
  readonly defaultBranding: SchoolBranding = {
    schoolName: APP.name,
    tagline: APP.tagline,
    logo: null,
    primaryColor: '#2563EB',
    secondaryColor: '#1E3A5F',
    sidebarColor: null,
    headerColor: null,
    loginBannerImage: null,
  };

  /** Load branding for a school slug (public — used on the login page). */
  async loadFromSlug(slug: string): Promise<void> {
    const data = await firstValueFrom(
      this.rawHttp
        .get<ApiResponse<SchoolBranding>>(
          `${environment.apiUrl}/public/branding/${encodeURIComponent(slug)}`,
        )
        .pipe(map((res) => this.unwrap(res))),
    );
    this.set(data);
  }

  /** Load branding for the logged-in user's tenant (after login). */
  async loadFromAuth(): Promise<void> {
    try {
      const data = await firstValueFrom(
        this.api.get<SchoolBranding>('/auth/tenant-branding'),
      );
      this.set(data);
    } catch {
      // A super admin (or a session without a tenant) has no tenant branding.
      this.resetToDefault();
    }
  }

  /** Apply an already-known branding object (e.g. while impersonating). */
  applyKnown(data: SchoolBranding): void {
    this.set(data);
  }

  /** Reset to the product default (super admin, or on logout). */
  resetToDefault(): void {
    this.branding.set(this.defaultBranding);
    this.applyBranding(this.defaultBranding, false);
    this.isLoaded.set(true);
  }

  // ── Internals ────────────────────────────────────────────────────────────────
  private set(data: SchoolBranding): void {
    this.branding.set(data);
    this.applyBranding(data, true);
    this.isLoaded.set(true);
  }

  /**
   * Write the branding onto :root CSS custom properties + page title.
   * `isSchool` = a real school theme (paint the sidebar with the accent);
   * false = the product default (keep the readable dark sidebar).
   */
  private applyBranding(b: SchoolBranding, isSchool: boolean): void {
    const root = document.documentElement;
    const primary = b.primaryColor || '#2563EB';

    // Override the EXISTING design tokens the whole app already consumes.
    root.style.setProperty('--color-primary', primary);
    root.style.setProperty('--color-primary-600', this.darken(primary, 0.12));
    root.style.setProperty('--color-primary-700', this.darken(primary, 0.24));
    root.style.setProperty('--color-primary-100', this.lighten(primary, 0.82));
    root.style.setProperty('--color-primary-050', this.lighten(primary, 0.92));

    // The left nav takes the school's accent colour (an explicit sidebarColor
    // wins). Active/hover states are generic translucent-white overlays (see
    // --sidebar-active-bg / --sidebar-hover-bg) so they read on any accent.
    // The product default keeps the dark slate sidebar.
    if (isSchool) {
      root.style.setProperty('--color-sidebar', b.sidebarColor || primary);
    } else {
      root.style.removeProperty('--color-sidebar');
    }

    // Spec alias tokens (consumed by the new platform components).
    root.style.setProperty('--primary-color', primary);
    root.style.setProperty('--primary-light', this.lighten(primary, 0.85));
    root.style.setProperty('--primary-dark', this.darken(primary, 0.2));
    root.style.setProperty('--sidebar-bg', b.sidebarColor || primary);
    root.style.setProperty('--header-bg', b.headerColor || '#FFFFFF');

    document.title = `${b.schoolName || APP.name} — ${b.tagline || APP.tagline}`;
  }

  private unwrap<T>(res: ApiResponse<T>): T {
    if (!res.success) {
      throw new Error(res.error?.message ?? 'Request failed');
    }
    return res.data;
  }

  // ── Hex colour helpers ───────────────────────────────────────────────────────
  /** Blend a hex colour toward white by `amount` (0..1). */
  private lighten(hex: string, amount: number): string {
    return this.mix(hex, 255, amount);
  }

  /** Blend a hex colour toward black by `amount` (0..1). */
  private darken(hex: string, amount: number): string {
    return this.mix(hex, 0, amount);
  }

  private mix(hex: string, target: number, amount: number): string {
    const rgb = this.toRgb(hex);
    if (!rgb) return hex;
    const t = Math.max(0, Math.min(1, amount));
    const ch = (c: number): number => Math.round(c + (target - c) * t);
    return this.toHex(ch(rgb[0]), ch(rgb[1]), ch(rgb[2]));
  }

  private toRgb(hex: string): [number, number, number] | null {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
    if (!m) return null;
    const int = parseInt(m[1], 16);
    return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
  }

  private toHex(r: number, g: number, b: number): string {
    const h = (c: number): string => c.toString(16).padStart(2, '0');
    return `#${h(r)}${h(g)}${h(b)}`;
  }
}
